import { Component } from '@theme/component';

/**
 * A slider locked to a question's set answers (blocks/_flow-step.liquid): the budget bands, or the
 * headcount stops on "How many people?".
 *
 * It answers nothing itself. The real inputs stay in the step and are what validation, the summary
 * and the HubSpot payload read: in `radio` mode the slider checks the matching option, in `number`
 * mode it writes the stop's value into the number field. So the values sent are the same fixed set
 * as before, without JavaScript the plain options still work, and "Not sure yet" keeps its own
 * control beside the slider.
 *
 * Nothing is selected until the visitor moves or taps it; the thumb's resting place is not an
 * answer.
 *
 * @typedef {{ value: string, label: string }} Stop
 * @typedef {{ input: HTMLInputElement, output: HTMLElement }} FlowRangeRefs
 *
 * @extends {Component<FlowRangeRefs>}
 */
class FlowRangeComponent extends Component {
  requiredRefs = ['input', 'output'];

  /** @type {Stop[]} */
  #stops = [];

  /** @type {HTMLElement | null} */
  #field = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#stops = JSON.parse(this.dataset.stops || '[]');
    } catch {
      this.#stops = [];
    }

    this.#field = this.closest('[data-field-name]');
    const { input } = this.refs;
    input.max = String(Math.max(0, this.#stops.length - 1));

    input.addEventListener('input', this.#select);
    // A tap on the thumb where it already sits fires no `input`, but it is still a choice.
    input.addEventListener('change', this.#select);
    this.#field?.addEventListener('change', this.#onFieldChange);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { input } = this.refs;
    input.removeEventListener('input', this.#select);
    input.removeEventListener('change', this.#select);
    this.#field?.removeEventListener('change', this.#onFieldChange);
  }

  #select = () => {
    const stop = this.#stops[Number(this.refs.input.value)];
    if (!stop) return;

    if (this.dataset.mode === 'number') {
      const unknown = this.#unknownCheckbox();
      if (unknown?.checked) {
        unknown.checked = false;
        // Runs the flow's own toggleUnknown, which re-enables the field.
        unknown.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const target = this.#numberInput();
      if (!target) return;
      target.disabled = false;
      target.value = stop.value;
      target.dispatchEvent(new Event('input', { bubbles: true }));
      target.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const radio = this.#radios().find((option) => option.value === stop.value);
      if (!radio) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#show(stop);
  };

  /**
   * "Not sure yet" chosen, or the answer changed some other way: follow it.
   *
   * @param {Event} event
   */
  #onFieldChange = (event) => {
    if (event.target === this.refs.input) return;
    this.#sync();
  };

  /** Reflects whatever the real inputs hold now. */
  #sync() {
    let current = '';
    if (this.dataset.mode === 'number') {
      const target = this.#numberInput();
      if (target && !target.disabled) current = target.value;
    } else {
      current = this.#radios().find((option) => option.checked)?.value ?? '';
    }

    const index = this.#stops.findIndex((stop) => stop.value === current);
    const stop = this.#stops[index];
    if (stop) {
      this.refs.input.value = String(index);
      this.#show(stop);
    } else {
      this.removeAttribute('data-selected');
      this.refs.output.textContent = this.dataset.placeholder ?? '';
      this.refs.input.removeAttribute('aria-valuetext');
    }
  }

  /**
   * @param {Stop} stop
   */
  #show(stop) {
    this.setAttribute('data-selected', '');
    this.refs.output.textContent = stop.label;
    this.refs.input.setAttribute('aria-valuetext', stop.label);
  }

  /** @returns {HTMLInputElement[]} */
  #radios() {
    return Array.from(this.#field?.querySelectorAll('input[type="radio"][data-option-input]') ?? []).filter(
      (element) => element instanceof HTMLInputElement
    );
  }

  /** @returns {HTMLInputElement | null} */
  #numberInput() {
    const element = this.#field?.querySelector(`input[name="${CSS.escape(this.dataset.target ?? '')}"]`);
    return element instanceof HTMLInputElement ? element : null;
  }

  /** @returns {HTMLInputElement | null} */
  #unknownCheckbox() {
    const element = this.#field?.querySelector(`input[name="${CSS.escape(`${this.dataset.target}_unknown`)}"]`);
    return element instanceof HTMLInputElement ? element : null;
  }
}

if (!customElements.get('flow-range')) {
  customElements.define('flow-range', FlowRangeComponent);
}
